import {
  createContext,
  PropsWithChildren,
  ReactNode,
  useContext,
  useMemo,
} from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { motion, MotionValue } from 'motion/react';
import { NavLink, useMatch } from 'react-router';

import { easeOutExpo } from '@components/primitives';

import { Command } from '@components/features/CommandMenu';
import { monitoring } from '@services/monitoring';

import css from './styles.module.css';

interface PageTabsContextType {
  group?: string;
}

const PageTabsContext = createContext<PageTabsContextType>({
  group: undefined,
});

export interface PageTabsItemProps {
  to: string;
  icon: ReactNode;
  label: string;
  keywords?: string[];
  end?: boolean;
  requiredFlag?: string;
  suffix?: ReactNode;
}

function PageTabsItem({
  to,
  label,
  end = false,
  icon,
  requiredFlag,
  keywords,
  suffix,
}: PageTabsItemProps) {
  const { group } = useContext(PageTabsContext);
  const flags = useFlags();

  const isActive = useMatch({ path: to, end });

  if (requiredFlag && !flags[requiredFlag]) {
    return null;
  }

  return (
    <li className={css.item}>
      <NavLink
        to={to}
        end={end}
        className={css.link}
        onClick={() =>
          monitoring.trackAction('navigation_click', { label, to })
        }
      >
        <span className={css.label}>{label}</span>
        {suffix}
      </NavLink>

      {isActive && (
        <motion.div
          className={css.indicator}
          layoutId={`page-tabs-${group}-indicator`}
          transition={{ duration: 0.3, ease: easeOutExpo }}
        />
      )}

      <Command
        type="route"
        id={`navigate-to-${to}`}
        icon={icon}
        group={group}
        keywords={keywords}
        label={label}
        href={to}
        context="app"
      />
    </li>
  );
}

export interface PageTabsRootProps extends PropsWithChildren {
  label?: string;
  leadingSpace?: MotionValue<number>;
}

function PageTabsRoot({ children, label, leadingSpace }: PageTabsRootProps) {
  const context = useMemo(() => ({ group: label }), [label]);

  return (
    <PageTabsContext.Provider value={context}>
      <div className={css.root}>
        {leadingSpace && (
          <motion.div
            className={css.leading}
            style={{ width: leadingSpace } as any}
          />
        )}
        <div className={css.rootInner}>
          <ul className={css.list}>{children}</ul>
        </div>
      </div>
    </PageTabsContext.Provider>
  );
}

export const PageTabs = Object.assign(PageTabsRoot, {
  Item: PageTabsItem,
});
